import {ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';

import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {StandardCodeDto, StandardTypeDto} from '../../../../generated-model/models';
import {GtNgDatepickerComponent} from '../../../shared/commons/gt-ng-datepicker/gt-ng-datepicker.component';
import {compareByNumericValue} from '../../../shared/commons/select-compare.util';
import {formatRecordedAt, resolveRecordedBy} from '../../../shared/commons/audit-display.util';
import {StandardTypeService} from '../standard-type/standard-type-service';

@Component({
  selector: 'app-standard-code-modal',
  standalone: true,
  imports: [ReactiveFormsModule, GtNgDatepickerComponent],
  templateUrl: './standard-code-modal.html',
  styleUrl: './standard-code-modal.scss'
})
export class StandardCodeModal implements OnInit {
  @Input() item: StandardCodeDto | undefined;
  @Input() viewOnly = false;
  @Input() onSave?: (payload: any, isEditMode: boolean) => Promise<boolean>;

  form: FormGroup;
  isEditMode = false;
  submitted = false;
  isSubmitting = false;
  recordedByDisplay = '-';
  recordedAtDisplay = '-';
  standardTypeOptions: StandardTypeDto[] = [];
  isStandardTypeLoading = false;
  readonly compareByNumericValue = compareByNumericValue;

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private standardTypeService: StandardTypeService,
    private cdr: ChangeDetectorRef
  ) {
    this.form = this.fb.group({
      id: [null],
      title: ['', [Validators.required]],
      titleEng: [''],
      preCode: [''],
      preCodeEng: [''],
      shortStandardCode: [''],
      yearStandardCode: [''],
      standardCode: [''],
      shortStandard: [''],
      standardType: [null],
      enforceStatus: [''],
      enforce: [''],
      startdate: [null],
      ordering: [0],
      state: [1],
      modifiedApiDate: [null],
      canceledDate: [null]
    });
  }

  ngOnInit(): void {
    if (this.item) {
      this.isEditMode = true;
      const selectedStandardTypeId = this.resolveStandardTypeId(this.item);
      this.seedStandardTypeOptionFromItem(this.item);
      this.recordedByDisplay = resolveRecordedBy(this.item);
      this.recordedAtDisplay = formatRecordedAt(this.item);
      this.form.patchValue({
        ...this.item,
        standardtype: selectedStandardTypeId,
        enforce: this.normalizeEnforceValue(this.item.enforce, this.item.enforceStatus),
        enforceStatus: this.normalizeEnforceStatus(this.item.enforce, this.item.enforceStatus)
      });
    } else {
      this.form.reset({ordering: 0, state: 1});
      this.recordedByDisplay = '-';
      this.recordedAtDisplay = '-';
    }

    this.loadStandardTypes();

    if (this.viewOnly) {
      this.form.disable();
    }

    this.cdr.detectChanges();
  }

  private loadStandardTypes(): void {
    this.isStandardTypeLoading = true;
    this.standardTypeService.getAllStandardTypes(0, 1000, '').subscribe({
      next: (page) => {
        this.standardTypeOptions = [...page.content].sort((a, b) => (a.ordering ?? 0) - (b.ordering ?? 0));
        const currentTypeId = this.toNumberOrNull(this.form.get('standardType')?.value) ?? this.resolveStandardTypeId(this.item);
        if (currentTypeId !== null) {
          this.form.get('standardType')?.setValue(currentTypeId, {emitEvent: false});
        }
        this.isStandardTypeLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        if (this.standardTypeOptions.length === 0) {
          this.standardTypeOptions = [];
        }
        this.isStandardTypeLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  save(): void {
    if (this.viewOnly) {
      return;
    }
    this.submitted = true;
    if (this.form.invalid) {
      return;
    }

    const rawValue = this.form.getRawValue();
    const enforceValue = this.normalizeEnforceValue(rawValue.enforce as string | null | undefined, rawValue.enforceStatus as string | null | undefined);
    const payload: StandardCodeDto = {
      ...rawValue,
      enforce: enforceValue,
      enforceStatus: this.normalizeEnforceStatus(enforceValue, rawValue.enforceStatus as string | null | undefined),
      startdate: this.toIsoDateString(rawValue.startdate),
      canceledDate: this.toIsoDateString(rawValue.canceledDate),
      modifiedApiDate: this.toIsoDateString(rawValue.modifiedApiDate)
    };

    void this.submitAndClose(payload);
  }

  private toIsoDateString(value?: string | null): string | undefined {
    if (!value) {
      return undefined;
    }
    if (value.includes('T')) {
      return value;
    }
    return value + 'T00:00:00.000Z';
  }

  private async submitAndClose(payload: any): Promise<void> {
    if (!this.onSave) {
      this.activeModal.close(payload);
      return;
    }

    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    try {
      const isSuccess = await this.onSave(payload, this.isEditMode);
      if (isSuccess) {
        this.activeModal.close(payload);
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  private normalizeEnforceValue(enforce?: string | null, enforceStatus?: string | null): string {
    const normalizedEnforce = (enforce ?? '').toString().trim();
    if (normalizedEnforce === '0' || normalizedEnforce === '1') {
      return normalizedEnforce;
    }

    const normalizedStatus = (enforceStatus ?? '').trim();
    if (normalizedStatus === 'สมัครใจ') {
      return '0';
    }
    if (normalizedStatus === 'บังคับ') {
      return '1';
    }

    return '';
  }

  private normalizeEnforceStatus(enforce?: string | null, enforceStatus?: string | null): string {
    const normalizedEnforce = (enforce ?? '').toString().trim();
    if (normalizedEnforce === '0') {
      return 'สมัครใจ';
    }
    if (normalizedEnforce === '1') {
      return 'บังคับ';
    }

    const normalizedStatus = (enforceStatus ?? '').trim();
    if (normalizedStatus === 'สมัครใจ' || normalizedStatus === 'บังคับ') {
      return normalizedStatus;
    }

    return '';
  }

  private toNumberOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
  }

  private resolveStandardTypeId(item?: StandardCodeDto): number | null {
    if (!item) {
      return null;
    }
    const standardtypeRaw = (item as any).standardtype;
    const raw =
      typeof standardtypeRaw === 'object' && standardtypeRaw !== null
        ? (standardtypeRaw as any).id
        : standardtypeRaw ?? (item as any).standardTypeId ?? (item as any).standardtypeId ?? (item as any).standardType?.id;
    return this.toNumberOrNull(raw);
  }

  private seedStandardTypeOptionFromItem(item: StandardCodeDto): void {
    const standardTypeRaw = (item as any).standardtype;
    if (typeof standardTypeRaw !== 'object' || standardTypeRaw === null) {
      return;
    }

    const id = this.toNumberOrNull((standardTypeRaw as any).id);
    if (id === null) {
      return;
    }

    const title = String((standardTypeRaw as any).title ?? '').trim() || `หมวด #${id}`;
    this.standardTypeOptions = [
      {
        id,
        title,
        ordering: 0,
        state: 1,
        checkedOut: 0,
        checkedOutTime: undefined as any
      } as StandardTypeDto
    ];
  }
}
